import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { onboardingManager } from '../business/onboarding/OnboardingManager';

/** Steps are grouped so the progress header stays readable as steps are added. */
export type OnboardingPhaseId = 'brand' | 'you' | 'area' | 'hours' | 'calendar';

export interface OnboardingStep {
  id: number;
  name: string;
  title: string;
  description: string;
  phase: OnboardingPhaseId;
  completed: boolean;
}

export interface OnboardingPhase {
  id: OnboardingPhaseId;
  title: string;
}

/**
 * A step registers one of these so the wizard can persist it before advancing.
 * Throwing rejects the step transition and surfaces the message to the user.
 */
export type StepSaver = () => Promise<void>;

interface OnboardingContextType {
  currentStep: number;
  steps: OnboardingStep[];
  phases: OnboardingPhase[];
  goToStep: (step: number) => void;
  nextStep: () => void;
  previousStep: () => void;
  completeStep: (stepId: number) => void;
  isStepAccessible: (stepId: number) => boolean;
  canProceed: boolean;
  setCanProceed: (value: boolean) => void;
  registerStepSaver: (stepId: number, saver: StepSaver | null) => void;
  getStepSaver: (stepId: number) => StepSaver | null;
  isModalOpen: boolean;
  openModal: () => void;
  closeModal: () => void;
  isInitializing: boolean;
  initializeProgress: (userId: string) => Promise<void>;
}

const OnboardingContext = createContext<OnboardingContextType | undefined>(undefined);

export const ONBOARDING_PHASES: OnboardingPhase[] = [
  { id: 'brand', title: 'Brand' },
  { id: 'you', title: 'You' },
  { id: 'area', title: 'Area' },
  { id: 'hours', title: 'Hours' },
  { id: 'calendar', title: 'Calendar' },
];

/**
 * One decision per screen. Titles double as the heading each step renders, so
 * the stepper and the page can never describe the same screen differently.
 */
const initialSteps: OnboardingStep[] = [
  {
    id: 0,
    name: 'brand',
    title: 'Your brand',
    description: 'The name, logo, and colors your customers see',
    phase: 'brand',
    completed: false,
  },
  {
    id: 1,
    name: 'profile',
    title: 'Your details',
    description: 'The name and number customers see when they book',
    phase: 'you',
    completed: false,
  },
  {
    id: 2,
    name: 'home',
    title: 'Home address',
    description: 'The address your routes are measured from',
    phase: 'you',
    completed: false,
  },
  {
    id: 3,
    name: 'base',
    title: 'Start & end of day',
    description: 'Where you head out from and where you finish',
    phase: 'area',
    completed: false,
  },
  {
    id: 4,
    name: 'radius',
    title: 'Service radius',
    description: 'How far you are willing to travel',
    phase: 'area',
    completed: false,
  },
  {
    id: 5,
    name: 'hours',
    title: 'Business hours',
    description: 'The days and hours you operate',
    phase: 'hours',
    completed: false,
  },
  {
    id: 6,
    name: 'travel',
    title: 'Travel time',
    description: 'Whether driving happens on the clock',
    phase: 'hours',
    completed: false,
  },
  {
    id: 7,
    name: 'calendar',
    title: 'Google Calendar',
    description: 'Pick the calendars that block your availability',
    phase: 'calendar',
    completed: false,
  },
];

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<OnboardingStep[]>(initialSteps);
  const [canProceed, setCanProceed] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);

  const hasInitializedRef = useRef(false);

  // Savers live in a ref so a step registering itself never triggers a render.
  const stepSaversRef = useRef<Map<number, StepSaver>>(new Map());

  const registerStepSaver = useCallback((stepId: number, saver: StepSaver | null) => {
    if (saver) {
      stepSaversRef.current.set(stepId, saver);
    } else {
      stepSaversRef.current.delete(stepId);
    }
  }, []);

  const getStepSaver = useCallback(
    (stepId: number) => stepSaversRef.current.get(stepId) ?? null,
    []
  );

  const initializeProgress = useCallback(async (userId: string) => {
    if (hasInitializedRef.current) return;
    hasInitializedRef.current = true;
    setIsInitializing(true);

    try {
      const { resumeStep, completedSteps } = await onboardingManager.determineResumeStep(userId);
      setSteps(prevSteps =>
        prevSteps.map(step => ({ ...step, completed: completedSteps.includes(step.id) }))
      );
      setCurrentStep(resumeStep);
    } catch (error) {
      console.error('Failed to initialize onboarding progress:', error);
      setCurrentStep(0);
    } finally {
      setIsInitializing(false);
    }
  }, []);

  const persistProgress = useCallback(async (step: number, completedStepIds: number[]) => {
    try {
      await onboardingManager.updateProgress(step, completedStepIds);
    } catch (error) {
      // Non-critical: progress is re-derived from real data on the next load.
      console.error('Failed to persist onboarding progress:', error);
    }
  }, []);

  const goToStep = useCallback(
    (step: number) => {
      if (step < 0 || step >= steps.length) return;
      setCurrentStep(step);
      persistProgress(
        step,
        steps.filter(s => s.completed).map(s => s.id)
      );
    },
    [steps, persistProgress]
  );

  const nextStep = useCallback(() => {
    if (currentStep >= steps.length - 1) return;
    const newStep = currentStep + 1;
    const newSteps = steps.map(step =>
      step.id === currentStep ? { ...step, completed: true } : step
    );
    setSteps(newSteps);
    setCurrentStep(newStep);
    persistProgress(
      newStep,
      newSteps.filter(s => s.completed).map(s => s.id)
    );
  }, [currentStep, steps, persistProgress]);

  const previousStep = useCallback(() => {
    if (currentStep <= 0) return;
    const newStep = currentStep - 1;
    setCurrentStep(newStep);
    persistProgress(
      newStep,
      steps.filter(s => s.completed).map(s => s.id)
    );
  }, [currentStep, steps, persistProgress]);

  const completeStep = useCallback(
    (stepId: number) => {
      setSteps(prevSteps => {
        if (prevSteps.find(s => s.id === stepId)?.completed) return prevSteps;
        const newSteps = prevSteps.map(step =>
          step.id === stepId ? { ...step, completed: true } : step
        );
        persistProgress(
          currentStep,
          newSteps.filter(s => s.completed).map(s => s.id)
        );
        return newSteps;
      });
    },
    [currentStep, persistProgress]
  );

  // Any visited-or-completed step stays reachable, so the stepper works as
  // back-navigation rather than as a set of locks the user can't reason about.
  const isStepAccessible = useCallback(
    (stepId: number) => {
      if (stepId <= currentStep) return true;
      for (let i = 0; i < stepId; i++) {
        if (!steps[i]?.completed) return false;
      }
      return true;
    },
    [steps, currentStep]
  );

  const openModal = useCallback(() => {
    setIsModalOpen(true);
    hasInitializedRef.current = false;
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const value: OnboardingContextType = {
    currentStep,
    steps,
    phases: ONBOARDING_PHASES,
    goToStep,
    nextStep,
    previousStep,
    completeStep,
    isStepAccessible,
    canProceed,
    setCanProceed,
    registerStepSaver,
    getStepSaver,
    isModalOpen,
    openModal,
    closeModal,
    isInitializing,
    initializeProgress,
  };

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
};

/**
 * Register the current step's save routine. The latest closure is always the
 * one invoked, so steps can read fresh state without re-registering.
 */
export const useStepSaver = (stepId: number, saver: StepSaver): void => {
  const { registerStepSaver } = useOnboarding();
  const saverRef = useRef(saver);
  saverRef.current = saver;

  useEffect(() => {
    registerStepSaver(stepId, () => saverRef.current());
    return () => registerStepSaver(stepId, null);
  }, [stepId, registerStepSaver]);
};

/**
 * Gate the footer's Continue button on the step's own validity.
 *
 * Steps used to do this with a bare effect, which left the previous step's
 * verdict in place for the first frame after a transition — long enough to
 * click Continue on a screen that had not been filled in yet. Resetting on
 * mount closes that window.
 */
export const useStepValidity = (isValid: boolean): void => {
  const { setCanProceed } = useOnboarding();

  useEffect(() => {
    setCanProceed(isValid);
    return () => setCanProceed(false);
  }, [isValid, setCanProceed]);
};
